<script setup lang="ts">
/**
 * Une enveloppe dans une liste (spec §8). Ligne 1 : nom et disponible. Ligne 2 : barre du
 * disponible restant sur le financement, pleine largeur. Ligne 3 : état explicite et financement.
 * Rien n'est porté par la couleur seule ; rien n'est tronqué hormis un nom très long.
 */
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

import { fr } from '@/copy/fr'
import type { EnvelopeHealth } from '@/domain'

import type { EnvelopeIconName } from '../icons/registry'
import Amount from './Amount.vue'
import Icon from './Icon.vue'
import ProgressBar from './ProgressBar.vue'
import StatusTag from './StatusTag.vue'

const props = withDefaults(
  defineProps<{
    name: string
    icon: EnvelopeIconName
    available: number
    funded: number
    health: EnvelopeHealth
    isProtected?: boolean
    to?: RouteLocationRaw
  }>(),
  { isProtected: false, to: undefined },
)

const tone = computed(() =>
  props.health === 'critical' ? 'danger' : props.health === 'watch' ? 'warn' : 'accent',
)
</script>

<template>
  <component
    :is="to ? RouterLink : 'div'"
    :to="to"
    :class="['flex gap-3 py-4', to ? 'transition-colors duration-80 active:bg-surface' : '']"
  >
    <span
      class="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface text-ink"
      aria-hidden="true"
    >
      <Icon :name="icon" :size="20" />
    </span>
    <span class="flex min-w-0 flex-1 flex-col gap-2">
      <span class="flex items-baseline justify-between gap-3">
        <span class="truncate text-body text-ink">{{ name }}</span>
        <Amount
          :value="available"
          size="section"
          :tone="available < 0 ? 'danger' : 'default'"
          class="shrink-0"
        />
      </span>
      <ProgressBar
        :value="available"
        :max="funded"
        :tone="tone"
        :label="fr.ui.envelopeProgress(name)"
      />
      <span class="flex flex-wrap items-center justify-between gap-2">
        <StatusTag v-if="health !== 'normal'" :status="health" />
        <StatusTag v-else-if="funded <= 0 && available === 0" status="unfunded" />
        <StatusTag v-else-if="isProtected" status="protected" />
        <span v-else />
        <span class="text-caption text-ink-muted">
          {{ fr.ui.outOf }} <Amount :value="funded" size="label" tone="muted" />
        </span>
      </span>
    </span>
  </component>
</template>
